import { GTM_EVENTS } from '@zg-rentals/google-tag-manager';
import { useQueryClient } from '@zg-rentals/react-query5';

import accessUtils from '../utils/accessUtils';

import PropertyConstants from '../constants/PropertyConstants';

import { emitConfirmCreateListingEvent, emitConfirmSeeAllActionsEvent } from '../analytics/events/addPropertyModal';

import { trackGtmEvent } from '../analytics';
import { useCreateProperty } from './queries/useCreateProperty';

import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';

const {
  PROPERTY_TYPES: { COMMUNITY },
  LISTING_TYPES: { ROOM, RENTAL },
} = PropertyConstants;

type ConfirmPropertyInput = {
  isRoomForRent?: boolean | string;
  propertyType?: string;
  fullAddress?: string;
  unit?: string;
  numUnits?: string;
  customerId?: string | null;
  duplicateAccountListingAlias?: string | null;
};

type UseConfirmPropertyParams = {
  user?: ZrmClickstreamUser & { features?: { canAccessModularListing?: boolean | null } };
  isSelectActionFinalStep?: boolean;
  onListingAliasSet?: (alias: string) => void;
};

export const useConfirmProperty = ({
  user,
  isSelectActionFinalStep = false,
  onListingAliasSet,
}: UseConfirmPropertyParams) => {
  const queryClient = useQueryClient();
  const { createProperty: createPropertyMutation } = useCreateProperty();

  const confirmProperty = async ({
    isRoomForRent,
    propertyType,
    fullAddress,
    unit,
    numUnits,
    customerId,
    duplicateAccountListingAlias,
  }: ConfirmPropertyInput): Promise<{
    responseData: GetUserPropertyDetailsResponseWebDto;
    canAccessModularListing: boolean;
  } | null> => {
    if (duplicateAccountListingAlias) {
      onListingAliasSet?.(duplicateAccountListingAlias);
      return null;
    }

    if (!fullAddress || !propertyType) {
      return null;
    }

    const listingType = isRoomForRent ? ROOM : RENTAL;
    const isCommunityType = propertyType === COMMUNITY;

    const responseData = await createPropertyMutation({
      listingType,
      propertyType,
      fullAddress,
      ...(isCommunityType ? { numUnits } : { unitNumber: unit }),
      customerId: listingType === ROOM ? null : customerId,
    });

    const canAccessModularListing = accessUtils.canAccessModularListing({
      isMultifamily: responseData.isMultifamily,
      listingTypeCode: responseData.listingType.code,
      lastActivatedMs: responseData.lastActivatedMs,
      userCanAccessModularListing: user?.features?.canAccessModularListing ?? false,
    });

    const clickstreamEvent =
      canAccessModularListing && isSelectActionFinalStep
        ? emitConfirmSeeAllActionsEvent
        : emitConfirmCreateListingEvent;

    if (user) {
      clickstreamEvent({ currentProperty: responseData, user });
    }
    trackGtmEvent({ event: GTM_EVENTS.RENTAL_MANAGER_ADD_PROPERTY });

    onListingAliasSet?.(responseData.listingAlias);

    void queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });

    return { responseData, canAccessModularListing };
  };

  return { confirmProperty };
};
